import Card from 'components/card/Card'
import Input from 'components/forms/Input'
import SimpleTabSideList from 'components/navbar/SimpleTabSideList'
import { difference, groupBy, intersection, isNil, keys, maxBy, meanBy, minBy, orderBy, round, uniq } from 'lodash'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { normalizeString } from 'utils/StringUtil'
import PropTypes from 'prop-types'
import DtoOperation from 'quality/components/operation/dto/DtoOperation'
import HydrobioMonitoringOperationGraph from 'quality/components/hydrobio/monitoring/HydrobioMonitoringOperationGraph'
import HydrobioMonitoringOverlayGraph from 'quality/components/hydrobio/monitoring/HydrobioMonitoringOverlayGraph'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import { Box, Grid, Stack } from '@mui/material'
import { IndiceRadarGraph } from 'quality/components/qualityComponents/IndiceGraph'
import { ALIGN, NewTable } from 'components/datatable/NewTable'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import useListIndexed from 'utils/customHook/useListIndexed'
import { ANTHROPOGENIC_PRESSURES, CHEMICAL_PRESSURES, LIST_INDEXES, LIST_PRESSURES } from 'quality/constants/HydrobioConstant'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import RadioButtons from 'components/forms/RadioButtons'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import { hybridSearch } from 'utils/SearchUtil'
import { PressureRadar } from '../operation/OperationHydrobioPressuresPanel'

const TAXON = 'TAXON'
const INDICE = 'INDICE'
const PRESSURE = 'PRESSURE'
const CRITERIA = {
    LAST: 'LAST',
    MAX: 'MAX',
    MIN: 'MIN',
    AVERAGE: 'AVERAGE',
}

const TabTaxon = ({
    listTaxons = [],
    selectedTaxons = [],
    setSelectedTaxons = () => { },
}) => {
    const {
        taxons,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
    }), shallowEqual)

    const [searchValue, setSearchValue] = useState('')

    const taxonsFormated = useMemo(() => listTaxons.map(code => {
        const isSelected = selectedTaxons.includes(code)

        const taxon = taxons.find(t => t.code === code)
        return {
            isCheck: isSelected ? (<CheckBoxIcon />) : (<CheckBoxOutlineBlankIcon />),
            taxon: taxon?.latinName || '',
            searchValue: normalizeString(`${code}${taxon?.name || ''}${taxon?.latinName || ''}`),
            code,
            isSelected,
        }
    }), [listTaxons, selectedTaxons, taxons])

    const taxonsFiltered = useMemo(() => {
        const searchCharacter = normalizeString(searchValue)
        return searchCharacter ? taxonsFormated.filter(t => t.searchValue.includes(searchCharacter)) : taxonsFormated
    }, [taxonsFormated, searchValue])

    const taxonsOrdered = useMemo(() => orderBy(taxonsFiltered, 'taxon'), [taxonsFiltered])

    const isAllSelected = taxonsFiltered.every(t => t.isSelected)

    const onSelectAll = () => {
        const selectListTaxon = taxonsFiltered.map(t => t.code)
        if (isAllSelected) {
            setSelectedTaxons(prev => difference(prev, selectListTaxon))
        } else {
            setSelectedTaxons(prev => uniq([...prev, ...selectListTaxon]))
        }
    }

    return (
        <Box>
            <Input
                title={i18n.search}
                value={searchValue}
                onChange={setSearchValue}
            />
            <NewTable
                headers={[{ key: 'isCheck', value: isAllSelected ? (<CheckBoxIcon />) : (<CheckBoxOutlineBlankIcon />), onClick: onSelectAll, width: 50, sticky: ALIGN.LEFT }, 'taxon']}
                rows={taxonsOrdered}
                onClickRow={({ code, isSelected }) => setSelectedTaxons(prev => {
                    if (isSelected) {
                        return prev.filter(p => p !== code)
                    }
                    return [...prev, code]
                })}
                maxHeight={'60vh'}
                rowsPerPageOptions={nbPerPageLabel}
            />
        </Box>
    )
}

TabTaxon.propTypes = {
    listTaxons: PropTypes.arrayOf(PropTypes.string),
    selectedTaxons: PropTypes.arrayOf(PropTypes.string),
    setSelectedTaxons: PropTypes.func,
}

const TabIndice = ({
    listIndices = [],
    criteriaRadar = {},
    setCriteriaRadar = () => {},
}) => {
    const { parameters } = useSelector(store => ({ parameters: store.ParameterReducer.parameters }), shallowEqual)
    const indexedParameters = useListIndexed(parameters, 'code')

    const [searchValue, setSearchValue] = useState('')

    const formattedIndices = listIndices.map(code => {
        const isSelected = criteriaRadar.indices?.includes(code)
        const parameter = indexedParameters[code]
        return {
            isCheck: isSelected ? (<CheckBoxIcon />) : (<CheckBoxOutlineBlankIcon />),
            indice: parameter?.labelWithCode || '',
            _normalizedSearchString: [code, parameter?.name, parameter?.shortLabel, parameter?.latinName].map(l => normalizeString(l)),
            code,
            isSelected,
        }
    })

    const filteredIndices = useMemo(() => {
        const subList = criteriaRadar.dataType === PRESSURE ? formattedIndices.filter(i => LIST_PRESSURES.includes(i.code)) : formattedIndices.filter(i => !LIST_PRESSURES.includes(i.code))
        return hybridSearch(subList, normalizeString(searchValue))
    }, [criteriaRadar.dataType, formattedIndices, searchValue])

    const isAllSelected = filteredIndices.every(i => i.isSelected)
    const onSelectAll = () => {
        setCriteriaRadar(p => {
            const selectedCode = filteredIndices.map(t => t.code)
            if (isAllSelected) return { ...p, indices: difference(p.indices, selectedCode) }
            return { ...p, indices: uniq([...p.indices, ...selectedCode]) }
        })
    }

    const criterias = useMemo(() => [
        { value: CRITERIA.LAST, label: i18n.lastData },
        { value: CRITERIA.MAX, label: i18n.max },
        { value: CRITERIA.MIN, label: i18n.min },
        { value: CRITERIA.AVERAGE, label: i18n.average },
    ], [])

    const dataToDisplay = useMemo(() => [
        { value: INDICE, label: i18n.indices },
        { value: PRESSURE, label: i18n.pressures },
    ], [])

    return (
        <Stack spacing={1}>
            <RadioButtons
                title
                elements={dataToDisplay}
                selected={criteriaRadar.dataType}
                onChange={v => setCriteriaRadar(p => ({ ...p, dataType: v }))}
            />
            <SuperMultiAutocomplete
                label={i18n.criteriaValue}
                values={criteriaRadar.usedCriteria}
                options={criterias}
                keyValue='value'
                onChange={v => setCriteriaRadar(p => ({ ...p, usedCriteria: v }))}
            />
            <Input
                title={i18n.search}
                value={searchValue}
                onChange={setSearchValue}
            />
            <NewTable
                headers={[{ key: 'isCheck', value: isAllSelected ? (<CheckBoxIcon />) : (<CheckBoxOutlineBlankIcon />), onClick: onSelectAll, width: 50, sticky: ALIGN.LEFT }, { key: 'indice', value: criteriaRadar.dataType === PRESSURE ? i18n.pressures : i18n.indices }]}
                rows={filteredIndices}

                onClickRow={({ code, isSelected }) => {
                    setCriteriaRadar(p => ({ ...p, indices: isSelected ? p.indices.filter(c => c !== code) : [...p.indices, code] }))
                }}
                maxHeight={'60vh'}
                rowsPerPageOptions={nbPerPageLabel}
            />
        </Stack>
    )
}

TabIndice.propTypes = {
    listIndices: PropTypes.arrayOf(PropTypes.string),
    criteriaRadar: PropTypes.shape({
        dataType: PropTypes.oneOf([INDICE, PRESSURE]),
        usedCriteria: PropTypes.oneOf([CRITERIA.LAST, CRITERIA.MAX, CRITERIA.MIN, CRITERIA.AVERAGE]),
        indices: PropTypes.arrayOf(PropTypes.string),
    }),
    setCriteriaRadar: PropTypes.func,
}

const HydrobioMonitoringGraph = ({
    hydrobioLists = [],
    hydrobioOperations = [],
    indices = [],
}) => {
    const [selectedTaxons, setSelectedTaxons] = useState([])
    const [criteriaRadar, setCriteriaRadar] = useState({ dataType: INDICE, usedCriteria: CRITERIA.LAST, indices: indices.map(i => i.parameter) })
    const listTaxonCodes = useMemo(() => uniq(hydrobioLists.map(l => l.taxon)), [hydrobioLists])
    const listIndiceCodes = useMemo(() => intersection(uniq(indices.map(i => i.parameter)), LIST_INDEXES), [indices])

    useUpdateEffect(() => {
        setSelectedTaxons([])
    }, [hydrobioLists, hydrobioOperations])

    const filteredIndices = useMemo(() => {
        const usedIndices = indices.filter(i => LIST_INDEXES.includes(i.parameter) && !isNil(i.result) && criteriaRadar.indices.includes(i.parameter))
        const groupedIndices = groupBy(usedIndices, 'parameter')
        return keys(groupedIndices).map(code => {
            const list = groupedIndices[code]
            if (criteriaRadar.usedCriteria === CRITERIA.MIN) return minBy(list, 'result')
            if (criteriaRadar.usedCriteria === CRITERIA.MAX) return maxBy(list, 'result')
            if (criteriaRadar.usedCriteria === CRITERIA.AVERAGE) return { parameter: code, result: round(meanBy(list, 'result'), 3) }
            return maxBy(list, 'sampleDate') // CRITERIA.LAST
        })
    }, [criteriaRadar.indices, criteriaRadar.usedCriteria, indices])

    const {
        listIndices = [],
        listChemicalPressures = [],
        listAnthropogenicPressures = [],
    } = useMemo(() => {
        return groupBy(filteredIndices, i => {
            if (CHEMICAL_PRESSURES.includes(i.parameter)) return 'listChemicalPressures'
            if (ANTHROPOGENIC_PRESSURES.includes(i.parameter)) return 'listAnthropogenicPressures'
            return 'listIndices'
        })
    }, [filteredIndices])

    return (
        <Card height={700}>
            <div className='row no-margin relative no-overflow'>
                <SimpleTabSideList
                    position='right'
                    defaultTab={TAXON}
                    tabs={[{
                        icon: 'bug_report',
                        constant: TAXON,
                        label: i18n.taxons,
                    }, {
                        icon: 'speed',
                        constant: INDICE,
                        label: i18n.indices,
                    }]}
                >
                    {tab => (
                        <>
                            {tab === TAXON && (
                                <TabTaxon
                                    listTaxons={listTaxonCodes}
                                    selectedTaxons={selectedTaxons}
                                    setSelectedTaxons={setSelectedTaxons}
                                />
                            )}
                            {tab === INDICE && (
                                <TabIndice
                                    listIndices={listIndiceCodes}
                                    criteriaRadar={criteriaRadar}
                                    setCriteriaRadar={setCriteriaRadar}
                                />
                            )}
                        </>
                    )}
                </SimpleTabSideList>
                <Grid container alignItems='center'>
                    <Grid size={8}>
                        {!selectedTaxons.length && (
                            <HydrobioMonitoringOperationGraph
                                hydrobioLists={hydrobioLists}
                                hydrobioOperations={hydrobioOperations}
                            />
                        )}
                        {!!selectedTaxons.length && (
                            <HydrobioMonitoringOverlayGraph
                                hydrobioLists={hydrobioLists}
                                selectedTaxons={selectedTaxons}
                            />
                        )}
                    </Grid>
                    <Grid size={4}>
                        {criteriaRadar.dataType === INDICE && listIndices.length !== 0 && (
                            <IndiceRadarGraph indices={listIndices} height={690} withTitle />
                        )}
                        {criteriaRadar.dataType === PRESSURE && listChemicalPressures.length !== 0 && listAnthropogenicPressures.length !== 0 && (
                            <>
                                <PressureRadar title={i18n.chemicalPressures} pressures={listChemicalPressures} />
                                <PressureRadar title={i18n.anthropogenicPressures} pressures={listAnthropogenicPressures} />
                            </>
                        )}
                    </Grid>
                </Grid>
            </div>
        </Card>
    )
}

HydrobioMonitoringGraph.propTypes = {
    hydrobioLists: PropTypes.arrayOf(PropTypes.shape({
        // DtoHydrobioList
        date: PropTypes.number,
        support: PropTypes.string,
        total: PropTypes.number,
    })),
    hydrobioOperations: PropTypes.arrayOf(PropTypes.instanceOf(DtoOperation)),
    indices: PropTypes.arrayOf(PropTypes.shape({

    })),
}

export default HydrobioMonitoringGraph